import api from "../lib/api";

/** Server endpoints exactly as provided */
const ENDPOINTS = {
  list: "/community/posts/", // GET ?category=&type=&page=
  create: "/community/posts/", // POST
  update: (id: string | number) => `/community/posts/${id}/`, // PUT/PATCH
  remove: (id: string | number) => `/community/posts/${id}/`, // DELETE
  like: (id: string | number) => `/community/posts/${id}/like/`, // POST
  react: (id: string | number) => `/community/posts/${id}/react/`, // POST {type}
  comment: (id: string | number) => `/community/posts/${id}/comment/`, // POST {content}
  share: (id: string | number) => `/community/posts/${id}/share/`, // POST
  copyLink: (id: string | number) => `/community/posts/${id}/copy_link/`, // GET
};

export type ApiUser = {
  id?: number | string;
  name?: string;
  avatar_url?: string | null;
  first_name?: string;
  last_name?: string;
  profile_photo?: string | null;
};

export type ApiComment = {
  id: string | number;
  author?: ApiUser;
  user?: ApiUser;
  content?: string;
  text?: string;
  created_at?: string;
  timestamp?: string;
  replies?: ApiComment[];
  user_full_name?: string;
};

export type ApiPost = {
  id: string | number;
  author?: ApiUser;
  user?: ApiUser;
  title?: string | null;
  content?: string;
  text?: string;
  category?: "explore" | "news" | "my_post" | "mentions";
  type?: "grant_subject" | "art" | "win" | "encouragement" | "question" | "more";
  badge?: "new" | "win" | "question" | "art";
  created_at?: string;
  timestamp?: string;
  likes?: number;
  likes_count?: number;
  is_liked?: boolean;
  liked?: boolean;
  comments?: ApiComment[];
  user_full_name?: string;
};

export async function fetchCommunityPosts(params: {
  category?: "explore" | "news" | "my_post" | "mentions";
  type?: string | null;
  page?: number;
}) {
  const { category = "explore", type, page = 1 } = params;
  const res = await api.get<ApiPost[]>(ENDPOINTS.list, {
    params: { category, type: type ?? undefined, page },
  });
  return res.data ?? [];
}

export async function createCommunityPost(payload: {
  title?: string | null;
  content: string;
  category: "explore" | "news" | "my_post" | "mentions";
  type?: "grant_subject" | "art" | "win" | "encouragement" | "question" | "more";
  mentions?: (number | string)[];
}) {
  const res = await api.post(ENDPOINTS.create, payload);
  return res.data;
}

export async function toggleLikePost(id: string | number) {
  const res = await api.post(ENDPOINTS.like(id), {});
  return res.data ?? {};
}

export async function addPostComment(id: string | number, content: string) {
  const res = await api.post(
    ENDPOINTS.comment(id),
    { content, post: id },
  );
  return res.data;
}

export async function addCommentReaction(id: string | number, type: string) {
  const res = await api.post<{ ok: boolean }>(
    ENDPOINTS.react(id),
    { type },
  );
  return res.data;
}

export async function reactToPost(id: string | number, type: "like" | "love" | "haha" | "wow" | "sad" | "angry") {
  const res = await api.post(ENDPOINTS.react(id), { type },);
  return res.data;
}

export async function sharePost(id: string | number) {
  const res = await api.post(ENDPOINTS.share(id), {},);
  return res.data;
}

export async function getCopyLink(id: string | number) {
  const res = await api.get<{ url?: string }>(ENDPOINTS.copyLink(id),);
  return res.data?.url || "";
}
